import {
  ClockIcon,
  EllipsisVIcon,
  FlagIcon,
  Link,
  PencilAltIcon,
  Pill,
  ProfileHeader,
  TimesCircleIcon,
  TrashIcon,
  useModal,
  useRouter,
} from '@forgeglobal/design-framework'
import { DeleteOpportunityModal } from 'components/opportunities/DeleteOpportunityModal'
import { OpportunityModal } from 'components/opportunities/OpportunityModal'
import { PopoverAction, PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { Link as RouterLink } from 'react-router-dom'
import { Routes } from 'util/Routes'
import {
  TradeIntentStatusType,
  TradeIntentType,
  useExpireOpportunityMutation,
  useOpportunityHeaderQuery,
} from 'graphql/bindings/AdminBindings'
import { formatAbbreviatedMoney, formatSharePrice } from 'util/NumberUtils'
import { formatShares } from 'util/ParsersFormatters'
import { getGravatarUrl } from 'util/GravatarUtil'
import { listingStructureDisplayName, opportunityStatusDisplayName } from 'util/OpportunityUtils'
import React, { Fragment } from 'react'
import capitalize from 'lodash/capitalize'
import compact from 'lodash/compact'
import moment from 'moment'

export const OpportunityHeader = () => {
  const { match } = useRouter<{ id: string }>()
  const { data } = useOpportunityHeaderQuery({ variables: { opportunityId: match.params.id } })
  const opportunity = data?.opportunity
  const account = opportunity?.account
  const company = opportunity?.company
  const tradeIntent = opportunity?.trade_intent
  const accountName = account?.name ?? ''
  const accountId = account?.id
  const companyName = company?.name ?? ''
  const issuerId = company?.crm_account.id ?? ''
  const companyLogo = company?.logo_url ?? ''
  const tradeIntentType = capitalize((tradeIntent?.type ?? TradeIntentType.Bid).toString())
  const acceptableStructures = (tradeIntent?.acceptable_structures ?? []).map(listingStructureDisplayName).join(', ')
  const pricePerShare = tradeIntent?.share_price
  const sharePriceText = pricePerShare ? ` at ${formatSharePrice(pricePerShare)} gross pps, ` : ''

  const shareAmountText =
    tradeIntent?.min_num_shares === tradeIntent?.max_num_shares
      ? `${formatShares(tradeIntent?.min_num_shares)} shares`
      : `${formatShares(tradeIntent?.min_num_shares)} to ${formatShares(tradeIntent?.max_num_shares)} shares`

  const dollarAmountText =
    tradeIntent?.min_dollar_amount === tradeIntent?.max_dollar_amount
      ? formatAbbreviatedMoney(tradeIntent?.min_dollar_amount)
      : `${formatAbbreviatedMoney(tradeIntent?.min_dollar_amount)} to ${formatAbbreviatedMoney(
          tradeIntent?.max_dollar_amount
        )}`

  const amountText = tradeIntent?.size_type === 'shares' ? shareAmountText : dollarAmountText

  const createdOn = moment(opportunity?.created_at ?? '').format('M/DD/YYYY')
  const latestInfo = opportunity?.latest_activity ?? ''
  const gravatarUrl = getGravatarUrl(account?.email ?? '')
  const source = opportunity?.source ?? ''
  const sourceText = source === 'crm_admin' ? 'Team' : 'Platform'

  const pills = compact([
    <Pill key="source" label={sourceText} />,
    <Pill key="status" label={opportunityStatusDisplayName(opportunity?.status!)} />,
  ])

  const [showEditModal, hideEditModal] = useModal(() => {
    return <OpportunityModal hideModal={hideEditModal} opportunityId={match.params.id} />
  }, [match.params.id])

  const [showDeleteModal, hideDeleteModal] = useModal(() => {
    return (
      <DeleteOpportunityModal
        hideModal={hideDeleteModal}
        opportunityId={match.params.id}
        pageSource="opportunityDetails"
      />
    )
  }, [match.params.id])

  const [expireOpportunity] = useExpireOpportunityMutation({})
  const onExpireOpportunityClick = () =>
    expireOpportunity({
      variables: { id: match.params.id },
    })

  const isLive = ![TradeIntentStatusType.Closed, TradeIntentStatusType.Cancelled].includes(
    opportunity?.status ?? TradeIntentStatusType.Pipeline
  )

  const headerActions = (
    <Fragment>
      <PopoverListItem button onClick={showEditModal}>
        <PopoverListItemIcon>
          <PencilAltIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Edit</PopoverListItemText>
      </PopoverListItem>
      {isLive && (
        <PopoverListItem button onClick={onExpireOpportunityClick}>
          <PopoverListItemIcon>
            <TimesCircleIcon />
          </PopoverListItemIcon>
          <PopoverListItemText>Expire</PopoverListItemText>
        </PopoverListItem>
      )}
      <PopoverListItem button onClick={showDeleteModal}>
        <PopoverListItemIcon>
          <TrashIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Delete</PopoverListItemText>
      </PopoverListItem>
    </Fragment>
  )

  const details = compact([
    {
      icon: <ClockIcon />,
      content: `Created on ${createdOn}`,
    },
    latestInfo && {
      icon: <FlagIcon />,
      content: latestInfo,
    },
  ])

  const control = <PopoverAction icon={<EllipsisVIcon />} actionMenu={headerActions} />

  return (
    <ProfileHeader
      title={
        <Fragment>
          {`${tradeIntentType}: `}
          <Link style={{ color: 'inherit' }} component={RouterLink} to={Routes.issuer(issuerId)}>
            {companyName}
          </Link>
        </Fragment>
      }
      subtitle={
        <Fragment>
          {`${amountText}${sharePriceText}${acceptableStructures} for `}
          <Link style={{ color: 'inherit' }} component={RouterLink} to={Routes.account(accountId)}>
            {accountName}
          </Link>
        </Fragment>
      }
      avatarImg={companyLogo}
      secondaryAvatarUrl={gravatarUrl}
      secondaryControl={control}
      details={details}>
      {pills}
    </ProfileHeader>
  )
}
